import React from "react";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import styled, { keyframes } from "styled-components";
import { XLg, ExclamationTriangleFill } from "react-bootstrap-icons";
import { dismissSubscriptionBanner } from "../login/userSession.redux";

const slideDown = keyframes`
  from { transform: translateY(-100%); opacity: 0; }
  to   { transform: translateY(0);     opacity: 1; }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Banner = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  gap: ${(p) => p.theme.spacing.md};
  padding: ${(p) => p.theme.spacing.sm} ${(p) => p.theme.spacing.md};
  background: ${(p) =>
    p.$severity === "warning" ? p.theme.color.warning : p.theme.color.primary};
  color: ${(p) => p.theme.color.surface};
  font-family: ${(p) => p.theme.font.family};
  font-size: ${(p) => p.theme.font.sizeMd};
  font-weight: ${(p) => p.theme.font.weightMedium};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  animation: ${slideDown} 0.25s ease-out;
`;

const Spinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: ${spin} 0.9s linear infinite;
  flex-shrink: 0;
`;

const Message = styled.div`
  flex: 1;
  line-height: 1.35;
`;

const RefreshBtn = styled.button`
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.6);
  color: inherit;
  cursor: pointer;
  padding: 4px 12px;
  border-radius: ${(p) => p.theme.radius.sm};
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
  }
`;

const CloseBtn = styled.button`
  background: transparent;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;

  &:hover {
    opacity: 0.75;
  }
`;

const SubscriptionActivatingBanner = ({
  subscriptionActivating,
  subscriptionActivatingDismissed,
  subscriptionActivatingTimedOut,
  dismissSubscriptionBanner,
}) => {
  const { t } = useTranslation();

  if (subscriptionActivatingDismissed) return null;
  if (!subscriptionActivating && !subscriptionActivatingTimedOut) return null;

  if (subscriptionActivatingTimedOut) {
    return (
      <Banner $severity="warning" role="status">
        <ExclamationTriangleFill size={18} aria-hidden />
        <Message>
          {t(
            "subscriptionActivation.timedOut",
            "Taking longer than expected. Refresh manually if your subscription doesn't show as active soon.",
          )}
        </Message>
        <RefreshBtn
          type="button"
          onClick={() => window.location.reload()}
        >
          {t("subscriptionActivation.refresh", "Refresh")}
        </RefreshBtn>
        <CloseBtn
          type="button"
          onClick={() => dismissSubscriptionBanner()}
          aria-label={t("feedback.common.close")}
        >
          <XLg size={16} aria-hidden />
        </CloseBtn>
      </Banner>
    );
  }

  return (
    <Banner role="status" aria-live="polite">
      <Spinner aria-hidden />
      <Message>
        {t(
          "subscriptionActivation.activating",
          "Activating your subscription…",
        )}
      </Message>
      <CloseBtn
        type="button"
        onClick={() => dismissSubscriptionBanner()}
        aria-label={t("feedback.common.close")}
      >
        <XLg size={16} aria-hidden />
      </CloseBtn>
    </Banner>
  );
};

export default connect(
  (state) => ({
    subscriptionActivating: state.userSession.subscriptionActivating,
    subscriptionActivatingDismissed:
      state.userSession.subscriptionActivatingDismissed,
    subscriptionActivatingTimedOut:
      state.userSession.subscriptionActivatingTimedOut,
  }),
  { dismissSubscriptionBanner },
)(SubscriptionActivatingBanner);
